// Penetration views — Category and Brand, each By Geo / By Age Band / By Gender.
// All endpoints share one shape (segment, users, universe, penetration_pct,
// users_total, panel_size, national_penetration_pct); the brand endpoints add
// an 'application' column (one row per app × segment) so the brand picker is
// scoped to the selected category and switching brands needs no refetch.
import { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { ThemeContext } from '../context/ThemeContext';
import { CategoryBehaviour } from '../utils/api';
import { BarChart, ChartCard, ChartLoader, ChartError, PickerSection } from '../components/Charts';
import { COLORS, PALETTE, AGE_ORDER, fmtNum, fmtMonth } from '../utils/theme';
import { POSTCODE_NAMES } from '../utils/postcodes';
import { UK_MAP_NAME as MAP_NAME, POSTCODE_REGION, REGION_POSTCODES, loadUKMap } from '../utils/ukMap';

const sortByAge = rows =>
  [...rows].sort((a, b) => AGE_ORDER.indexOf(a.segment) - AGE_ORDER.indexOf(b.segment));

const sortByPenDesc = rows =>
  [...rows].sort((a, b) => b.penetration_pct - a.penetration_pct);


function PenetrationBase({
  sectionTitle, dimLabel, apiFn,
  topN = 0, barHeight = 300, labelMap = null, brandMode = false, compare = false, sortFn = null,
  compareCallout = false,
}) {
  const { category } = useCategory();
  const { month }    = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType, setSessionType] = useState('open_app');
  const [segment,     setSegment]     = useState('');
  const [app,         setApp]         = useState('');
  const [compareSel,    setCompareSel]    = useState([]);
  const [pickerOpen,    setPickerOpen]    = useState(false);
  const [compareBrands, setCompareBrands] = useState([]);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;
  const display       = s => labelMap?.[s] || s;

  const { data, loading, error } = useApi(
    () => apiFn({ category: effectiveCat, session_type: sessionType, month_id: selectedMonth }),
    { effectiveCat, sessionType, selectedMonth },
    !!selectedMonth,
  );

  // Brand mode: rows arrive ordered by app size — first app is the category leader
  const apps = useMemo(
    () => (brandMode ? [...new Set((data || []).map(r => r.application))] : []),
    [data, brandMode],
  );
  const selApp = brandMode ? (apps.includes(app) ? app : apps[0] || '') : '';

  // Rows for the selected entity; optionally re-sorted (e.g. age ascending)
  const rows = useMemo(() => {
    if (!data) return [];
    const filtered = brandMode ? data.filter(r => r.application === selApp) : data;
    return sortFn ? sortFn(filtered) : filtered;
  }, [data, brandMode, selApp, sortFn]);

  const national = rows[0] || null;
  const shown    = useMemo(() => (topN ? rows.slice(0, topN) : rows), [rows, topN]);

  const selSegment = segment || shown[0]?.segment || '';
  const segRow     = rows.find(r => r.segment === selSegment);
  const gap        = segRow && national
    ? +(segRow.penetration_pct - national.national_penetration_pct).toFixed(2)
    : null;

  const entity   = brandMode ? selApp : effectiveCat;
  const subStart = brandMode ? `${selApp} · ${effectiveCat}` : effectiveCat;

  const penSeries = useMemo(() => [{
    name:  'Penetration %',
    color: COLORS.teal,
    data:  shown.map(r => r.segment === selSegment
      ? { value: r.penetration_pct, itemStyle: { color: COLORS.blue } }
      : r.penetration_pct),
  }], [shown, selSegment]);

  const gapSeries = useMemo(() => {
    if (!national) return [];
    return [{
      name: 'Gap vs National (pp)',
      data: shown.map(r => {
        const g = +(r.penetration_pct - national.national_penetration_pct).toFixed(2);
        return { value: g, itemStyle: { color: g >= 0 ? COLORS.positive : COLORS.negative } };
      }),
    }];
  }, [shown, national]);

  // ── Multi-select comparison ─────────────────────────────────────────────
  // Segment options: union across all brands in brandMode (some apps miss segments)
  const segmentOptions = useMemo(
    () => brandMode ? [...new Set((data || []).map(r => r.segment))] : rows.map(r => r.segment),
    [data, rows, brandMode],
  );
  const compareSegs = useMemo(
    () => segmentOptions.filter(s => compareSel.includes(s)),
    [segmentOptions, compareSel],
  );
  const rowsBySeg = useMemo(() => Object.fromEntries(rows.map(r => [r.segment, r])), [rows]);
  const byAppSeg  = useMemo(() => {
    const m = {};
    (data || []).forEach(r => { if (r.application) m[`${r.application}__${r.segment}`] = r; });
    return m;
  }, [data]);

  const toggleCompare = seg =>
    setCompareSel(prev => prev.includes(seg) ? prev.filter(s => s !== seg) : [...prev, seg]);
  const toggleCompareBrand = a =>
    setCompareBrands(prev => prev.includes(a) ? prev.filter(b => b !== a) : [...prev, a]);

  // One teal/signed series normally; one series per chosen brand in brandMode.
  // Gap is always vs the entity's own national penetration.
  const comparePenSeries = useMemo(() => {
    if (!compareSegs.length) return [];
    if (!brandMode) return [{
      name:  'Penetration %',
      color: COLORS.teal,
      data:  compareSegs.map(s => rowsBySeg[s]?.penetration_pct ?? 0),
    }];
    return compareBrands.map((a, i) => ({
      name:  a,
      color: PALETTE[i % PALETTE.length],
      data:  compareSegs.map(s => byAppSeg[`${a}__${s}`]?.penetration_pct ?? 0),
    }));
  }, [brandMode, compareSegs, compareBrands, rowsBySeg, byAppSeg]);

  const compareGapSeries = useMemo(() => {
    if (!compareSegs.length) return [];
    if (!brandMode) {
      if (!national) return [];
      return [{
        name: 'Gap vs National (pp)',
        data: compareSegs.map(s => {
          const r = rowsBySeg[s];
          const g = r ? +(r.penetration_pct - national.national_penetration_pct).toFixed(2) : 0;
          return { value: g, itemStyle: { color: g >= 0 ? COLORS.positive : COLORS.negative } };
        }),
      }];
    }
    return compareBrands.map((a, i) => ({
      name:  a,
      color: PALETTE[i % PALETTE.length],
      data:  compareSegs.map(s => {
        const r = byAppSeg[`${a}__${s}`];
        return r ? +(r.penetration_pct - r.national_penetration_pct).toFixed(2) : 0;
      }),
    }));
  }, [brandMode, compareSegs, compareBrands, rowsBySeg, byAppSeg, national]);

  const compareReady  = compareSegs.length > 0 && (!brandMode || compareBrands.length > 0);
  const compareHeight = Math.max(
    180,
    compareSegs.length * (brandMode ? Math.max(compareBrands.length, 1) * 18 + 18 : 36) + 100,
  );

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      {/* ── Brand picker (brand mode only) ─────────────────────────── */}
      {brandMode && apps.length > 0 && (
        <div style={{
          background: 'rgba(29,184,154,0.06)',
          border: '1px solid rgba(29,184,154,0.18)',
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.teal, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Choose a brand to explore
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {apps.map((a, i) => {
              const active = a === selApp;
              return (
                <button
                  key={a}
                  onClick={() => setApp(a)}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 100,
                    border: active ? `2px solid ${PALETTE[i % PALETTE.length]}` : '1.5px solid #E5E7EB',
                    background: active ? PALETTE[i % PALETTE.length] : 'transparent',
                    color: active ? '#fff' : '#374151',
                    fontWeight: active ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {!brandMode && (
          <>
            <span className="filter-label">{dimLabel}</span>
            <select className="filter-select" value={selSegment} onChange={e => setSegment(e.target.value)}>
              {rows.map(r => <option key={r.segment} value={r.segment}>{display(r.segment)}</option>)}
            </select>
          </>
        )}
      </div>

      <div className="trends-section-label">
        {brandMode ? 'Brand' : 'Category'} Penetration — {sectionTitle}
      </div>

      {loading && <ChartLoader height={120} />}
      {error   && <ChartError message={error} height={120} />}

      {!loading && !error && national && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">{entity} Penetration — National</div>
            <div className="kpi-value">{national.national_penetration_pct}%</div>
            <div className="kpi-delta neutral">
              {fmtNum(national.users_total)} of {fmtNum(national.panel_size)} panel users
            </div>
          </div>
          {segRow && (
            <div className="kpi-card">
              <div className="kpi-label">{display(selSegment)} Penetration</div>
              <div className="kpi-value">{segRow.penetration_pct}%</div>
              <div className="kpi-delta neutral">
                {fmtNum(segRow.users)} of {fmtNum(segRow.universe)} in {display(selSegment)}
              </div>
            </div>
          )}
          {gap != null && (
            <div className="kpi-card">
              <div className="kpi-label">{display(selSegment)} Gap vs National</div>
              <div className="kpi-value" style={{ color: gap >= 0 ? COLORS.positive : COLORS.negative }}>
                {gap >= 0 ? '+' : ''}{gap} pp
              </div>
              <div className="kpi-delta neutral">
                {gap >= 0 ? 'over-penetrated' : 'under-penetrated'} vs {national.national_penetration_pct}% national
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !error && shown.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <ChartCard
            title={`${sectionTitle} — Penetration`}
            sub={`${subStart} · ${fmtMonth(selectedMonth)} · national ${national?.national_penetration_pct}% · selection in blue`}
          >
            <BarChart
              categories={shown.map(r => display(r.segment))}
              series={penSeries}
              horizontal
              xFormatter={v => `${v}%`}
              height={barHeight}
            />
          </ChartCard>

          <ChartCard
            title={`${sectionTitle} — Gap vs National`}
            sub={`${subStart} · ${fmtMonth(selectedMonth)} · percentage points above (green) / below (red) national penetration`}
          >
            <BarChart
              categories={shown.map(r => display(r.segment))}
              series={gapSeries}
              horizontal
              xFormatter={v => `${v} pp`}
              height={barHeight}
            />
          </ChartCard>
        </div>
      )}

      {!loading && !error && shown.length === 0 && <div className="empty-state">No data</div>}

      {/* Multi-select comparison — pick segments (and brands in brandMode) */}
      {compare && !loading && !error && rows.length > 0 && (
        <PickerSection
          callout={compareCallout || null}
          style={{ marginTop: compareCallout ? 28 : 16 }}
        >
          <div className="card-header">
            <div>
              <div className="card-title">{brandMode ? `Compare Brands & ${dimLabel}s` : `Compare ${dimLabel}s`}</div>
              <div className="card-sub">
                {compareReady
                  ? brandMode
                    ? `${compareBrands.length} brand${compareBrands.length > 1 ? 's' : ''} × ${compareSegs.length} ${dimLabel.toLowerCase()}${compareSegs.length > 1 ? 's' : ''} · gap vs each brand's own national %`
                    : `${compareSegs.length} ${dimLabel.toLowerCase()}${compareSegs.length > 1 ? 's' : ''} selected · national ${national?.national_penetration_pct}%`
                  : brandMode
                    ? `Select brands and ${dimLabel.toLowerCase()}s below to compare penetration and gaps`
                    : `Select ${dimLabel.toLowerCase()}s below to compare penetration and gaps vs national`}
              </div>
            </div>
            {brandMode && (
              <button
                className="btn-icon"
                style={{ width: 'auto', padding: '0 10px', fontSize: 11, gap: 4, marginRight: 6 }}
                onClick={() => { setBrandPickerOpen(o => !o); setPickerOpen(false); }}
              >
                {brandPickerOpen ? '▲' : '▼'} Choose Brands
              </button>
            )}
            <button
              className="btn-icon"
              style={{ width: 'auto', padding: '0 10px', fontSize: 11, gap: 4 }}
              onClick={() => { setPickerOpen(o => !o); setBrandPickerOpen(false); }}
            >
              {pickerOpen ? '▲' : '▼'} Choose {dimLabel}s
            </button>
            {(compareSel.length > 0 || compareBrands.length > 0) && (
              <button
                className="btn-icon"
                style={{ marginLeft: 6 }}
                onClick={() => { setCompareSel([]); setCompareBrands([]); }}
              >
                ✕
              </button>
            )}
          </div>

          {brandPickerOpen && (
            <div className="geo-picker">
              {apps.map(a => (
                <label key={a} className={`geo-picker-item ${compareBrands.includes(a) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={compareBrands.includes(a)}
                    onChange={() => toggleCompareBrand(a)}
                    style={{ marginRight: 6 }}
                  />
                  {a}
                </label>
              ))}
            </div>
          )}

          {pickerOpen && (
            <div className="geo-picker">
              {segmentOptions.map(s => (
                <label key={s} className={`geo-picker-item ${compareSel.includes(s) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={compareSel.includes(s)}
                    onChange={() => toggleCompare(s)}
                    style={{ marginRight: 6 }}
                  />
                  {display(s)}
                </label>
              ))}
            </div>
          )}

          {compareReady && (
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div className="trends-section-label" style={{ marginBottom: 8 }}>Penetration %</div>
                  <BarChart
                    categories={compareSegs.map(display)}
                    series={comparePenSeries}
                    horizontal
                    xFormatter={v => `${v}%`}
                    height={compareHeight}
                  />
                </div>
                <div>
                  <div className="trends-section-label" style={{ marginBottom: 8 }}>
                    {brandMode ? "Gap vs each brand's National (pp)" : 'Gap vs National (pp)'}
                  </div>
                  <BarChart
                    categories={compareSegs.map(display)}
                    series={compareGapSeries}
                    horizontal
                    xFormatter={v => `${v} pp`}
                    height={compareHeight}
                  />
                </div>
              </div>
            </div>
          )}
        </PickerSection>
      )}
    </div>
  );
}

// ── Category Penetration ──────────────────────────────────────────────────

export function PenetrationGeoView() {
  const { dark }     = useContext(ThemeContext);
  const { category } = useCategory();
  const { month }    = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType,    setSessionType]    = useState('open_app');
  const [mapReady,       setMapReady]       = useState(false);
  const [geoError,       setGeoError]       = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('London');

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;

  useEffect(() => { loadUKMap(ok => { setMapReady(ok); setGeoError(!ok); }); }, []);

  const { data, loading, error } = useApi(
    () => CategoryBehaviour.penetrationByGeo({ category: effectiveCat, session_type: sessionType, month_id: selectedMonth }),
    { effectiveCat, sessionType, selectedMonth },
    !!selectedMonth,
  );

  const national = data?.[0] || null;

  // Aggregate postcode penetration_pct → region average for map coloring
  const { regionData, maxRegionVal } = useMemo(() => {
    if (!data?.length) return { regionData: [], maxRegionVal: 1 };
    const acc = {};
    data.forEach(r => {
      const region = POSTCODE_REGION[r.segment];
      if (!region) return;
      if (!acc[region]) acc[region] = { sum: 0, count: 0 };
      acc[region].sum   += r.penetration_pct || 0;
      acc[region].count += 1;
    });
    const regionData   = Object.entries(acc).map(([name, { sum, count }]) => ({ name, value: +(sum / count).toFixed(2) }));
    const maxRegionVal = Math.max(1, ...regionData.map(d => d.value));
    return { regionData, maxRegionVal };
  }, [data]);

  // Filter to clicked region's postcodes (or all if none selected)
  const filteredRows = useMemo(() => {
    if (!data?.length || !selectedRegion) return data || [];
    const codes = new Set(REGION_POSTCODES[selectedRegion] || []);
    return data.filter(r => codes.has(r.segment));
  }, [data, selectedRegion]);

  // Top 15 by penetration %
  // Top 15 by penetration — same order used by both charts for easy row-by-row comparison
  const top15 = useMemo(
    () => [...filteredRows].sort((a, b) => b.penetration_pct - a.penetration_pct).slice(0, 15),
    [filteredRows]
  );

  const penSeries = useMemo(() => [{
    name: 'Penetration %',
    color: COLORS.teal,
    data: top15.map(r => r.penetration_pct),
  }], [top15]);

  const gapSeries = useMemo(() => {
    if (!national) return [];
    return [{
      name: 'Gap vs National (pp)',
      data: top15.map(r => {
        const g = +(r.penetration_pct - r.national_penetration_pct).toFixed(2);
        return { value: g, itemStyle: { color: g >= 0 ? COLORS.positive : COLORS.negative } };
      }),
    }];
  }, [top15, national]);

  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>Avg penetration: <b>${p.value != null ? p.value + '%' : '—'}</b>`,
        backgroundColor: dark ? '#1e2633' : '#fff',
        borderColor: dark ? '#2d3748' : '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: dark ? '#F9FAFB' : '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.12);border-radius:8px;',
      },
      visualMap: {
        min: 0,
        max: maxRegionVal,
        text: [`${maxRegionVal}%`, '0%'],
        realtime: false,
        calculable: true,
        inRange: { color: ['#FDE68A', '#F59E0B', '#22C55E', '#15803D'] },
        textStyle: { color: dark ? '#9CA3AF' : '#6B7280', fontSize: 10 },
        right: 8,
        top: 'middle',
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: MAP_NAME,
        roam: true,
        data: regionData,
        selectedMode: 'single',
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        label: { show: false },
        itemStyle: {
          areaColor: dark ? '#1e2633' : '#E8ECF4',
          borderColor: dark ? '#374151' : '#fff',
          borderWidth: 0.8,
        },
      }],
    };
  }, [mapReady, regionData, maxRegionVal, dark]);

  const onMapClick = useCallback(e => {
    const region = e?.name;
    if (!region) return;
    setSelectedRegion(prev => prev === region ? null : region);
  }, []);

  const chartHeight = Math.max(200, top15.length * 28 + 60);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => { setSessionType(e.target.value); setSelectedRegion('London'); }}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {selectedRegion && selectedRegion !== 'London' && (
          <button
            onClick={() => setSelectedRegion('London')}
            style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)', borderRadius: 100, padding: '4px 12px', cursor: 'pointer' }}
          >
            ✕ {selectedRegion}
          </button>
        )}
      </div>

      {/* ── National KPI ───────────────────────────────────────────── */}
      {!loading && !error && national && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">{effectiveCat} — National Penetration</div>
            <div className="kpi-value">{national.national_penetration_pct}%</div>
            <div className="kpi-delta neutral">
              {fmtNum(national.users_total)} of {fmtNum(national.panel_size)} panel users
            </div>
          </div>
        </div>
      )}

      {/* ── Map + charts grid ──────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 16,
        alignItems: 'stretch',
      }}>
        {/* Choropleth — avg penetration % by region */}
        <ChartCard
          title="Penetration % by Region"
          sub={`${effectiveCat} · ${fmtMonth(selectedMonth)} · avg postcode penetration · click a region to drill down`}
        >
          {geoError && <ChartError message="Failed to load UK map data" height={520} />}
          {!geoError && (loading || !mapReady) && <ChartLoader height={520} />}
          {!geoError && mapReady && !loading && error && <ChartError message={error} height={520} />}
          {!geoError && mapReady && !loading && !error && mapOption && (
            regionData.length > 0 ? (
              <ReactECharts
                key={selectedRegion ?? 'full'}
                echarts={echarts}
                option={mapOption}
                style={{ height: 520 }}
                onEvents={{ click: onMapClick }}
                notMerge
              />
            ) : (
              <div className="empty-state" style={{ height: 520 }}>No data for selected filters</div>
            )
          )}
        </ChartCard>

        {/* Top 15 penetration + Gap vs National — side by side, same order */}
        {loading && <ChartLoader height={520} />}
        {!loading && error && <ChartError message={error} height={520} />}
        {!loading && !error && (
          <ChartCard
            title={`Top 15 Penetration — ${selectedRegion ?? 'All Regions'}`}
            sub={`${effectiveCat} · ${fmtMonth(selectedMonth)} · national ${national?.national_penetration_pct}%`}
          >
            {top15.length === 0
              ? <div className="empty-state">No postcode data{selectedRegion ? ` for ${selectedRegion}` : ''}</div>
              : <BarChart
                  categories={top15.map(r => POSTCODE_NAMES[r.segment] || r.segment)}
                  series={penSeries}
                  horizontal
                  xFormatter={v => `${v}%`}
                  height={520}
                />
            }
          </ChartCard>
        )}
        {!loading && !error && (
          <ChartCard
            title={`Gap vs National — ${selectedRegion ?? 'All Regions'}`}
            sub={`${effectiveCat} · ${fmtMonth(selectedMonth)} · pp vs ${national?.national_penetration_pct}% national · green = over, red = under`}
          >
            {top15.length === 0
              ? <div className="empty-state">No postcode data{selectedRegion ? ` for ${selectedRegion}` : ''}</div>
              : <BarChart
                  categories={top15.map(r => POSTCODE_NAMES[r.segment] || r.segment)}
                  series={gapSeries}
                  horizontal
                  xFormatter={v => `${v} pp`}
                  height={520}
                />
            }
          </ChartCard>
        )}
      </div>
    </div>
  );
}

export function PenetrationAgeView() {
  return (
    <PenetrationBase
      sectionTitle="By Age Band"
      dimLabel="Age Band"
      apiFn={CategoryBehaviour.penetrationByAge}
      barHeight={280}
      sortFn={sortByAge}
    />
  );
}

export function PenetrationGenderView() {
  return (
    <PenetrationBase
      sectionTitle="By Gender"
      dimLabel="Gender"
      apiFn={CategoryBehaviour.penetrationByGender}
      barHeight={200}
      labelMap={{ M: 'Male', F: 'Female' }}
    />
  );
}

// ── Brand Penetration ─────────────────────────────────────────────────────

export function BrandPenetrationGeoView() {
  const { dark }     = useContext(ThemeContext);
  const { category } = useCategory();
  const { month }    = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType,    setSessionType]    = useState('open_app');
  const [app,            setApp]            = useState('');
  const [mapReady,       setMapReady]       = useState(false);
  const [geoError,       setGeoError]       = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('London');

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;

  useEffect(() => { loadUKMap(ok => { setMapReady(ok); setGeoError(!ok); }); }, []);

  const { data, loading, error } = useApi(
    () => CategoryBehaviour.brandPenetrationByGeo({ category: effectiveCat, session_type: sessionType, month_id: selectedMonth }),
    { effectiveCat, sessionType, selectedMonth },
    !!selectedMonth,
  );

  const apps   = useMemo(() => [...new Set((data || []).map(r => r.application))], [data]);
  const selApp = apps.includes(app) ? app : (apps[0] || '');

  // Rows for the selected brand
  const brandRows = useMemo(
    () => (data || []).filter(r => r.application === selApp),
    [data, selApp]
  );
  const national = brandRows[0] || null;

  // Aggregate brand's penetration → region average for map coloring
  const { regionData, maxRegionVal } = useMemo(() => {
    if (!brandRows.length) return { regionData: [], maxRegionVal: 1 };
    const acc = {};
    brandRows.forEach(r => {
      const region = POSTCODE_REGION[r.segment];
      if (!region) return;
      if (!acc[region]) acc[region] = { sum: 0, count: 0 };
      acc[region].sum   += r.penetration_pct || 0;
      acc[region].count += 1;
    });
    const regionData   = Object.entries(acc).map(([name, { sum, count }]) => ({ name, value: +(sum / count).toFixed(2) }));
    const maxRegionVal = Math.max(1, ...regionData.map(d => d.value));
    return { regionData, maxRegionVal };
  }, [brandRows]);

  // Filter to clicked region's postcodes
  const filteredRows = useMemo(() => {
    if (!brandRows.length || !selectedRegion) return brandRows;
    const codes = new Set(REGION_POSTCODES[selectedRegion] || []);
    return brandRows.filter(r => codes.has(r.segment));
  }, [brandRows, selectedRegion]);

  // Top 15 by penetration — same order used by both charts for easy row-by-row comparison
  const top15 = useMemo(
    () => [...filteredRows].sort((a, b) => b.penetration_pct - a.penetration_pct).slice(0, 15),
    [filteredRows]
  );

  const penSeries = useMemo(() => [{
    name: 'Penetration %',
    color: COLORS.teal,
    data: top15.map(r => r.penetration_pct),
  }], [top15]);

  const gapSeries = useMemo(() => {
    if (!national) return [];
    return [{
      name: 'Gap vs National (pp)',
      data: top15.map(r => {
        const g = +(r.penetration_pct - r.national_penetration_pct).toFixed(2);
        return { value: g, itemStyle: { color: g >= 0 ? COLORS.positive : COLORS.negative } };
      }),
    }];
  }, [top15, national]);

  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>Avg penetration: <b>${p.value != null ? p.value + '%' : '—'}</b>`,
        backgroundColor: dark ? '#1e2633' : '#fff',
        borderColor: dark ? '#2d3748' : '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: dark ? '#F9FAFB' : '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.12);border-radius:8px;',
      },
      visualMap: {
        min: 0,
        max: maxRegionVal,
        text: [`${maxRegionVal}%`, '0%'],
        realtime: false,
        calculable: true,
        inRange: { color: ['#FDE68A', '#F59E0B', '#22C55E', '#15803D'] },
        textStyle: { color: dark ? '#9CA3AF' : '#6B7280', fontSize: 10 },
        right: 8,
        top: 'middle',
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: MAP_NAME,
        roam: true,
        data: regionData,
        selectedMode: 'single',
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        label: { show: false },
        itemStyle: {
          areaColor: dark ? '#1e2633' : '#E8ECF4',
          borderColor: dark ? '#374151' : '#fff',
          borderWidth: 0.8,
        },
      }],
    };
  }, [mapReady, regionData, maxRegionVal, dark]);

  const onMapClick = useCallback(e => {
    const region = e?.name;
    if (!region) return;
    setSelectedRegion(prev => prev === region ? null : region);
  }, []);

  const chartHeight = Math.max(200, top15.length * 28 + 60);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      {/* ── Brand picker ───────────────────────────────────────────── */}
      {apps.length > 0 && (
        <div style={{
          background: dark ? 'rgba(29,184,154,0.07)' : 'rgba(29,184,154,0.06)',
          border: `1px solid ${dark ? 'rgba(29,184,154,0.20)' : 'rgba(29,184,154,0.18)'}`,
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.teal, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Choose a brand to explore
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {apps.map((a, i) => {
              const active = a === selApp;
              return (
                <button
                  key={a}
                  onClick={() => { setApp(a); setSelectedRegion('London'); }}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 100,
                    border: active ? `2px solid ${PALETTE[i % PALETTE.length]}` : `1.5px solid ${dark ? '#374151' : '#E5E7EB'}`,
                    background: active ? PALETTE[i % PALETTE.length] : 'transparent',
                    color: active ? '#fff' : (dark ? '#D1D5DB' : '#374151'),
                    fontWeight: active ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => { setSessionType(e.target.value); setSelectedRegion('London'); }}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {selectedRegion && selectedRegion !== 'London' && (
          <button
            onClick={() => setSelectedRegion('London')}
            style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)', borderRadius: 100, padding: '4px 12px', cursor: 'pointer' }}
          >
            ✕ {selectedRegion}
          </button>
        )}
      </div>

      {/* ── National KPI ───────────────────────────────────────────── */}
      {!loading && !error && national && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">{selApp} — National Penetration</div>
            <div className="kpi-value">{national.national_penetration_pct}%</div>
            <div className="kpi-delta neutral">
              {fmtNum(national.users_total)} of {fmtNum(national.panel_size)} panel users
            </div>
          </div>
        </div>
      )}

      {/* ── Map + charts grid ──────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 16,
        alignItems: 'stretch',
      }}>
        <ChartCard
          title={`${selApp} Penetration % by Region`}
          sub={`${effectiveCat} · ${fmtMonth(selectedMonth)} · avg postcode penetration · click a region to drill down`}
        >
          {geoError && <ChartError message="Failed to load UK map data" height={520} />}
          {!geoError && (loading || !mapReady) && <ChartLoader height={520} />}
          {!geoError && mapReady && !loading && error && <ChartError message={error} height={520} />}
          {!geoError && mapReady && !loading && !error && mapOption && (
            regionData.length > 0 ? (
              <ReactECharts
                key={`${selApp}-${selectedRegion ?? 'full'}`}
                echarts={echarts}
                option={mapOption}
                style={{ height: 520 }}
                onEvents={{ click: onMapClick }}
                notMerge
              />
            ) : (
              <div className="empty-state" style={{ height: 520 }}>No data for selected filters</div>
            )
          )}
        </ChartCard>

        {/* Top 15 penetration + Gap vs National — side by side, same order */}
        {loading && <ChartLoader height={520} />}
        {!loading && error && <ChartError message={error} height={520} />}
        {!loading && !error && (
          <ChartCard
            title={`Top 15 Penetration — ${selectedRegion ?? 'All Regions'}`}
            sub={`${selApp} · ${fmtMonth(selectedMonth)} · national ${national?.national_penetration_pct}%`}
          >
            {top15.length === 0
              ? <div className="empty-state">No postcode data{selectedRegion ? ` for ${selectedRegion}` : ''}</div>
              : <BarChart
                  categories={top15.map(r => POSTCODE_NAMES[r.segment] || r.segment)}
                  series={penSeries}
                  horizontal
                  xFormatter={v => `${v}%`}
                  height={520}
                />
            }
          </ChartCard>
        )}
        {!loading && !error && (
          <ChartCard
            title={`Gap vs National — ${selectedRegion ?? 'All Regions'}`}
            sub={`${selApp} · ${fmtMonth(selectedMonth)} · pp vs ${national?.national_penetration_pct}% national · green = over, red = under`}
          >
            {top15.length === 0
              ? <div className="empty-state">No postcode data{selectedRegion ? ` for ${selectedRegion}` : ''}</div>
              : <BarChart
                  categories={top15.map(r => POSTCODE_NAMES[r.segment] || r.segment)}
                  series={gapSeries}
                  horizontal
                  xFormatter={v => `${v} pp`}
                  height={520}
                />
            }
          </ChartCard>
        )}
      </div>
    </div>
  );
}

export function BrandPenetrationAgeView() {
  return (
    <PenetrationBase
      sectionTitle="By Age Band"
      dimLabel="Age Band"
      apiFn={CategoryBehaviour.brandPenetrationByAge}
      barHeight={280}
      brandMode
      compareCallout={{
        title: 'Compare Brands Across Age Bands',
        subtitle: 'Select age bands and brands below to analyse penetration side by side',
        highlights: [
          { label: 'Multi-select', desc: 'Pick any age bands and brands to compare simultaneously in a single view.' },
          { label: 'Brand vs brand', desc: 'See how each selected brand indexes across age bands side by side.' },
          { label: 'Gap vs national', desc: "Each brand's penetration gap per age band against its own national baseline." },
        ],
      }}
      compare
      sortFn={sortByAge}
    />
  );
}

export function BrandPenetrationGenderView() {
  return (
    <PenetrationBase
      sectionTitle="By Gender"
      dimLabel="Gender"
      apiFn={CategoryBehaviour.brandPenetrationByGender}
      barHeight={200}
      labelMap={{ M: 'Male', F: 'Female' }}
      brandMode
      compareCallout={{
        title: 'Compare Brands Across Genders',
        subtitle: 'Select genders and brands below to compare penetration side by side',
        highlights: [
          { label: 'Gender breakdown', desc: 'Compare how multiple brands reach Male and Female audiences simultaneously.' },
          { label: 'Gap vs national', desc: "See each brand's gender split relative to its own national penetration." },
          { label: 'Cross-brand view', desc: 'Overlay any combination of brands for direct gender-level comparison.' },
        ],
      }}
      compare
    />
  );
}
